/**
 * Utility functions for formatting data in the dashboard
 */
import dayjs from 'dayjs';
import 'dayjs/locale/fa';

/**
 * Format a date to a readable string
 * @param date The date to format
 * @param format The format string (default: 'YYYY-MM-DD HH:mm:ss')
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string | number, format: string = 'YYYY-MM-DD HH:mm:ss'): string => {
  // Get current locale from document
  const locale = document.documentElement.lang === 'fa' ? 'fa' : 'en';
  return dayjs(date).locale(locale).format(format);
};

/**
 * Format a number with thousands separators
 * @param num The number to format
 * @param options Intl.NumberFormat options
 * @returns Formatted number string
 */
export const formatNumber = (num: number, options: Intl.NumberFormatOptions = {}): string => {
  const locale = document.documentElement.lang === 'fa' ? 'fa-IR' : 'en-US';
  return new Intl.NumberFormat(locale, options).format(num);
};

/**
 * Format a number as a currency
 * @param amount The amount to format
 * @param currency The currency code (default: 'USD')
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  const locale = document.documentElement.lang === 'fa' ? 'fa-IR' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format a file size in bytes to a human-readable string
 * @param bytes The size in bytes
 * @param decimals The number of decimal places (default: 2)
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Format a duration in milliseconds to a human-readable string
 * @param ms The duration in milliseconds
 * @returns Formatted duration string
 */
export const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;

  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

/**
 * Format Wei value to ETH
 * @param weiValue The value in Wei as string or number
 * @param decimals The number of decimal places (default: 6)
 * @returns Formatted ETH value as string
 */
export const formatWeiToEth = (weiValue: string | number, decimals: number = 6): string => {
  if (!weiValue) return '0';

  try {
    // Convert to BigInt to handle large numbers properly
    const wei = BigInt(weiValue.toString());

    // 1 ETH = 10^18 Wei
    const ethValue = Number(wei) / 1e18;

    // Format with specified decimals
    return ethValue.toFixed(decimals);
  } catch (error) {
    console.error('Error formatting Wei to ETH:', error);
    return '0';
  }
};
